import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Breadcrumb, Card, Col, Row, Badge, Spinner, Alert, Button, Form } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import {
    applyOptimisticPinToTasks,
    applyOptimisticPinToTeamGroups,
    formatGanttDateTime,
    formatTimelineScopeRange,
    snapToTeamTwoHourSlot,
    stampGanttQueueOrder,
} from './ganttHelpers';
import TeamStyleGanttChart, {
    useTeamStyleTimeline,
    userFullName,
} from '../../components/TeamStyleGanttChart';
import { parsePersonUserId } from '../../utils/mapToGanttTasks';
import '../../assets/css/user-gantt.css';
import '../../assets/css/team-planner.css';
import '../../assets/css/timeline-ui.css';

const WORK_DAY_HOURS = 8;

const PRIORITY_BADGE = {
    Urgent: 'danger',
    High: 'warning',
    Medium: 'info',
    Low: 'secondary',
};

const formatWorkDuration = (hours) => {
    const total = Math.round((Number(hours) || 0) * 10) / 10;
    if (total <= 0) return '0h';
    const days = Math.floor(total / WORK_DAY_HOURS);
    const rem = Math.round((total - days * WORK_DAY_HOURS) * 10) / 10;
    if (days > 0 && rem > 0) return `${days}d ${rem}h`;
    if (days > 0) return `${days}d`;
    return `${rem}h`;
};

const TeamPlanner = () => {
    const { accessToken } = useAuth();
    const navigate = useNavigate();

    const [groups, setGroups] = useState([]);
    const [unassignedCards, setUnassignedCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [messageVariant, setMessageVariant] = useState('info');
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [highlightedCardId, setHighlightedCardId] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);

    const dragRef = useRef(null);

    const loadTeam = useCallback(
        async ({ background = false } = {}) => {
            if (!accessToken) return null;
            if (background) setRefreshing(true);
            else setLoading(true);
            setError('');
            try {
                const res = await axios.get(`${config.apiBASEURL}/TaskBoardRoutes/gantt/team`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const nextGroups = (res.data?.groups || []).map((g) => ({
                    ...g,
                    members: (g.members || []).map((m) => ({
                        ...m,
                        tasks: stampGanttQueueOrder(
                            (m.tasks || []).map((t) => ({
                                ...t,
                                gantt_user_id: t.gantt_user_id || m.user?.user_id,
                            }))
                        ),
                    })),
                }));
                setGroups(nextGroups);
                setUnassignedCards(res.data?.unassigned_cards || []);
                return { groups: nextGroups, unassigned: res.data?.unassigned_cards || [] };
            } catch (err) {
                console.error('Error loading team planner:', err);
                setError(err.response?.data?.message || 'Could not load the team planner.');
                return null;
            } finally {
                if (background) setRefreshing(false);
                else setLoading(false);
            }
        },
        [accessToken]
    );

    useEffect(() => {
        loadTeam();
    }, [loadTeam]);

    const members = useMemo(() => {
        const list = [];
        groups.forEach((g) =>
            (g.members || []).forEach((m) =>
                list.push({ ...m, role: g.user_type })
            )
        );
        return list;
    }, [groups]);

    const allTasks = useMemo(() => members.flatMap((m) => m.tasks || []), [members]);
    const { rangeStart, rangeEnd } = useTeamStyleTimeline(allTasks, 1);

    const matchesSearch = useCallback(
        (m) => {
            const q = search.trim().toLowerCase();
            if (!q) return true;
            if (userFullName(m.user).toLowerCase().includes(q)) return true;
            return (m.tasks || []).some((t) => (t.title || '').toLowerCase().includes(q));
        },
        [search]
    );

    const flash = (text, variant = 'info') => {
        setMessage(text);
        setMessageVariant(variant);
    };

    const chartMembers = useMemo(
        () => members.filter(matchesSearch),
        [members, matchesSearch]
    );

    const onBarDragStart = (e, task, fromUserId) => {
        e.stopPropagation();
        const uid = fromUserId == null ? null : Number(fromUserId);
        const snapshot = {
            cardId: Number(task.id || task.card_id),
            fromUserId: uid,
            title: task.title,
            fromPool: fromUserId == null,
            task,
        };
        dragRef.current = snapshot;
        e.dataTransfer.effectAllowed = 'move';
        try {
            e.dataTransfer.setData('text/plain', String(task.id || task.card_id));
        } catch (_) {
            /* some browsers require setData */
        }
    };

    const onBarDragEnd = () => {
        dragRef.current = null;
        setDropTarget(null);
    };

    const applyOptimisticMove = (fromUserId, toUserId, cardId, index) => {
        setGroups((prev) =>
            prev.map((g) => ({
                ...g,
                members: (g.members || []).map((m) => {
                    const uid = Number(m.user.user_id);
                    if (uid !== fromUserId && uid !== toUserId) return m;
                    let tasks = [...(m.tasks || [])];
                    let moved = null;
                    if (uid === fromUserId) {
                        const i = tasks.findIndex((t) => Number(t.id) === cardId);
                        if (i >= 0) [moved] = tasks.splice(i, 1);
                    }
                    if (uid === toUserId) {
                        if (!moved) {
                            const src = members.find(
                                (mm) => Number(mm.user.user_id) === fromUserId
                            );
                            moved = (src?.tasks || []).find((t) => Number(t.id) === cardId) || null;
                        }
                        if (moved) {
                            tasks = tasks.filter((t) => Number(t.id) !== cardId);
                            const at = Math.max(0, Math.min(index, tasks.length));
                            tasks.splice(at, 0, moved);
                        }
                    }
                    return { ...m, tasks };
                }),
            }))
        );
    };

    const handleDropOnUser = async (e, toUserId, targetTasks) => {
        e.preventDefault();
        const drag = dragRef.current;
        setDropTarget(null);
        if (!drag) return;

        const fromUserId = drag.fromUserId;
        const cardId = drag.cardId;
        const dropMoment = snapToTeamTwoHourSlot(
            drag.task?.scheduled_start_at || drag.task?.planned_start_at || new Date()
        );
        const index = (targetTasks || []).filter((t) => Number(t.id) !== cardId).length;
        const fromMember = members.find((m) => Number(m.user.user_id) === fromUserId);
        const toMember = members.find((m) => Number(m.user.user_id) === Number(toUserId));

        dragRef.current = null;
        setSaving(true);
        setMessage('');

        try {
            if (drag.fromPool || fromUserId == null) {
                const poolTask =
                    drag.task ||
                    unassignedCards.find((t) => Number(t.id || t.card_id) === cardId);
                if (!poolTask) {
                    setSaving(false);
                    return;
                }
                const preferred = dropMoment.toISOString();
                setUnassignedCards((prev) =>
                    prev.filter((t) => Number(t.id || t.card_id) !== cardId)
                );
                const pinnedPool = applyOptimisticPinToTasks(
                    [{ ...poolTask, id: poolTask.id || poolTask.card_id }],
                    cardId,
                    preferred
                )[0];
                setGroups((prev) =>
                    prev.map((g) => ({
                        ...g,
                        members: (g.members || []).map((m) => {
                            if (Number(m.user?.user_id) !== Number(toUserId)) return m;
                            const nextTasks = [...(m.tasks || [])].filter(
                                (t) => Number(t.id) !== cardId
                            );
                            nextTasks.splice(
                                Math.max(0, Math.min(index, nextTasks.length)),
                                0,
                                pinnedPool
                            );
                            return { ...m, tasks: nextTasks };
                        }),
                    }))
                );

                await axios.post(
                    `${config.apiBASEURL}/TaskBoardRoutes/schedule/assign-from-pool`,
                    {
                        card_id: cardId,
                        to_user_id: Number(toUserId),
                        target_index: index,
                        preferred_start_at: preferred,
                        allow_outside_window: true,
                    },
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );
                flash(
                    `Assigned “${poolTask.title}” to ${userFullName(
                        toMember?.user || {}
                    )} and pinned its start — their later tasks shift after it.`,
                    'success'
                );
                setHighlightedCardId(cardId);
                setTimeout(() => setHighlightedCardId(null), 5000);
                await loadTeam({ background: true });
            } else if (fromUserId === Number(toUserId)) {
                const task = (fromMember?.tasks || []).find((t) => Number(t.id) === cardId);
                if (!task) {
                    setSaving(false);
                    return;
                }
                const preferred = dropMoment.toISOString();
                setGroups((prev) =>
                    applyOptimisticPinToTeamGroups(prev, fromUserId, cardId, preferred)
                );
                const pinRes = await axios.post(
                    `${config.apiBASEURL}/TaskBoardRoutes/schedule/pin-start`,
                    {
                        user_id: fromUserId,
                        card_id: cardId,
                        preferred_start_at: preferred,
                        allow_outside_window: true,
                    },
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );
                flash(
                    pinRes.data?.message ||
                        `Pinned “${task.title}” on ${userFullName(fromMember.user)}'s timeline.`,
                    pinRes.data?.shifted ? 'warning' : 'success'
                );
                setHighlightedCardId(cardId);
                setTimeout(() => setHighlightedCardId(null), 5000);
                await loadTeam({ background: true });
            } else {
                applyOptimisticMove(fromUserId, Number(toUserId), cardId, index);
                await axios.post(
                    `${config.apiBASEURL}/TaskBoardRoutes/schedule/reassign`,
                    {
                        card_id: cardId,
                        from_user_id: fromUserId,
                        to_user_id: Number(toUserId),
                        target_index: index,
                        preferred_start_at: dropMoment.toISOString(),
                        allow_outside_window: true,
                    },
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );
                flash(
                    `Moved “${drag.title || 'task'}” from ${userFullName(
                        fromMember?.user || {}
                    )} to ${userFullName(toMember?.user || {})}. Their later tasks shifted to run after it.`,
                    'success'
                );
                setHighlightedCardId(cardId);
                setTimeout(() => setHighlightedCardId(null), 5000);
                await loadTeam({ background: true });
            }
        } catch (err) {
            console.error('Reassign/pin/assign failed', err);
            flash(err.response?.data?.message || 'Could not save the change.', 'danger');
            await loadTeam({ background: true });
        } finally {
            setSaving(false);
        }
    };

    const savePinStart = async (task, preferredStartAt, userId, allocatedHours = null) => {
        const uid = Number(userId);
        if (!uid || !task?.id) return false;
        setGroups((prev) =>
            applyOptimisticPinToTeamGroups(prev, uid, task.id, preferredStartAt, allocatedHours, false)
        );
        setSaving(true);
        setMessage('');
        try {
            const pinRes = await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/schedule/pin-start`,
                {
                    user_id: uid,
                    card_id: Number(task.id),
                    preferred_start_at: preferredStartAt,
                    allow_outside_window: true,
                    bump_others: false,
                    ...(allocatedHours != null ? { allocated_hours: allocatedHours } : {}),
                },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            flash(
                pinRes.data?.message || `Pinned “${task.title}”.`,
                pinRes.data?.shifted ? 'warning' : 'success'
            );
            setHighlightedCardId(Number(task.id));
            setTimeout(() => setHighlightedCardId(null), 5000);
            return true;
        } catch (err) {
            flash(err.response?.data?.message || 'Could not pin start time.', 'danger');
            await loadTeam({ background: true });
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handlePersonDrop = (e, ganttTask, phase) => {
        const uid = parsePersonUserId(ganttTask);
        if (!uid) return;
        if (phase === 'over') {
            if (!dragRef.current) return;
            setDropTarget((prev) => (prev?.userId === uid ? prev : { userId: uid }));
            return;
        }
        const member = members.find((m) => Number(m.user?.user_id) === Number(uid));
        handleDropOnUser(e, uid, member?.tasks || []);
    };

    const visibleUnassigned = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return unassignedCards;
        return unassignedCards.filter((card) => {
            const hay = [
                card.title,
                card.board_name,
                card.project_name,
                card.brand_name,
                card.list_name,
                card.priority,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [unassignedCards, search]);

    return (
        <Row className="body_content user-gantt-page team-planner-page">
            <Col lg={12} className="mb-3">
                <Breadcrumb>
                    <Breadcrumb.Item onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
                        <i className="pi pi-angle-left" /> Back
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>Team Planner</Breadcrumb.Item>
                </Breadcrumb>
            </Col>

            <Col lg={12}>
                <Card>
                    <Card.Body>
                        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                            <Form.Control
                                type="search"
                                size="sm"
                                placeholder="Search people or tasks…"
                                style={{ maxWidth: 280 }}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            {unassignedCards.length > 0 && (
                                <Badge bg="warning" text="dark">
                                    {unassignedCards.length} unassigned
                                </Badge>
                            )}
                            {saving && <Spinner animation="border" size="sm" />}
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => loadTeam()}
                            >
                                Refresh
                            </Button>
                        </div>

                        {message && (
                            <Alert variant={messageVariant} className="py-2 small">
                                {message}
                            </Alert>
                        )}

                        {loading && (
                            <div className="text-center py-5">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading team planner…
                            </div>
                        )}

                        {!loading && error && <p className="text-danger">{error}</p>}

                        {!loading &&
                            !error &&
                            chartMembers.length === 0 &&
                            unassignedCards.length === 0 && (
                                <p className="text-muted mb-0">No team members match your search.</p>
                            )}

                        {!loading &&
                            !error &&
                            (chartMembers.length > 0 || unassignedCards.length > 0) && (
                            <TeamStyleGanttChart
                                title="Team planner"
                                subtitle={`Everyone's tasks on one timeline · ${formatTimelineScopeRange(
                                    rangeStart,
                                    rangeEnd
                                )}`}
                                activeCount={allTasks.length}
                                members={chartMembers}
                                highlightedCardIds={highlightedCardId}
                                dropTarget={dropTarget}
                                onDateChange={async (ganttTask) => {
                                    const source = ganttTask?.sourceTask;
                                    const uid = ganttTask?.sourceUserId;
                                    if (!source) return false;
                                    const start =
                                        ganttTask.start instanceof Date
                                            ? ganttTask.start.toISOString()
                                            : ganttTask.start;
                                    return savePinStart(
                                        source,
                                        start,
                                        uid,
                                        ganttTask.isResize ? ganttTask.hours : null
                                    );
                                }}
                                    onPersonDrop={handlePersonDrop}
                                    onTaskListDragStart={(e, ganttTask) => {
                                        if (!ganttTask?.sourceTask) return;
                                        onBarDragStart(
                                            e,
                                            ganttTask.sourceTask,
                                            ganttTask.sourceUserId
                                        );
                                    }}
                                    onTaskListDragEnd={onBarDragEnd}
                                    onBarClick={(t) => {
                                        if (t?.board_id && t?.id) {
                                            navigate(
                                                `/Graphe/dashboard/kanbanboard/${t.board_id}?card=${t.id}`
                                            );
                                        }
                                    }}
                                    refreshing={refreshing || saving}
                                    emptyMessage="No people match your search."
                                    sidePanel={
                                        <aside className="team-unassigned-pool">
                                            <div className="team-unassigned-header">
                                                <strong>Unassigned</strong>
                                                <Badge bg="secondary" className="ms-2">
                                                    {visibleUnassigned.length}
                                                </Badge>
                                            </div>
                                            <p className="team-unassigned-hint">
                                                Drag a card onto a person in the task list to
                                                assign and pin its start.
                                            </p>
                                            {visibleUnassigned.length === 0 ? (
                                                <p className="text-muted small mb-0 px-2">
                                                    {unassignedCards.length === 0
                                                        ? 'No unassigned open tasks.'
                                                        : 'No unassigned tasks match your search.'}
                                                </p>
                                            ) : (
                                                <ul className="team-unassigned-list">
                                                    {visibleUnassigned.map((card) => {
                                                        const id = Number(card.id || card.card_id);
                                                        return (
                                                            <li
                                                                key={id}
                                                                className="team-unassigned-card"
                                                                draggable
                                                                onDragStart={(e) =>
                                                                    onBarDragStart(e, card, null)
                                                                }
                                                                onDragEnd={onBarDragEnd}
                                                                title="Drag onto a person in the list to assign"
                                                            >
                                                                <div className="team-unassigned-title">
                                                                    {card.title}
                                                                </div>
                                                                <div className="team-unassigned-meta">
                                                                    {[
                                                                        card.project_name,
                                                                        card.board_name,
                                                                        card.list_name,
                                                                    ]
                                                                        .filter(Boolean)
                                                                        .join(' · ') ||
                                                                        'Board task'}
                                                                </div>
                                                                <div className="team-unassigned-meta d-flex flex-wrap gap-1 align-items-center">
                                                                    {card.on_hold && (
                                                                        <Badge bg="secondary">
                                                                            On hold
                                                                        </Badge>
                                                                    )}
                                                                    {card.priority && (
                                                                        <Badge
                                                                            bg={
                                                                                PRIORITY_BADGE[
                                                                                    card.priority
                                                                                ] || 'secondary'
                                                                            }
                                                                            className="text-capitalize"
                                                                        >
                                                                            {card.priority}
                                                                        </Badge>
                                                                    )}
                                                                    {card.deadline_at && (
                                                                        <span>
                                                                            Due{' '}
                                                                            {formatGanttDateTime(
                                                                                card.deadline_at
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                    {card.estimated_hours !=
                                                                        null && (
                                                                        <span>
                                                                            {formatWorkDuration(
                                                                                card.estimated_hours
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                        </aside>
                                    }
                                />
                        )}
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default TeamPlanner;
